import { cx } from "class-variance-authority";

import { ChevronLeftIcon } from "@/assets/icons/general/ChevronLeft";
import { ChevronRightIcon } from "@/assets/icons/general/ChevronRight";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Typography } from "@/components/ui/text/Typography/Typography";

interface NewPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

export const NewPagination = ({ currentPage, pageSize, totalItems, onPageChange }: NewPaginationProps) => {
  // Calculate the range display (e.g., "1-10 of 21")
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);
  const totalPages = Math.ceil(totalItems / pageSize);

  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  if (!totalItems || totalItems === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Typography
        size="body-3"
        variant="default"
        className="text-text-default-primary"
      >
        {startItem}-{endItem} of {totalItems}
      </Typography>

      <IconButton
        icon={ChevronLeftIcon}
        variant="transparent"
        size="small"
        className="!p-1"
        iconClassName={cx("w-5 h-5 text-text-default-primary", !canGoPrevious && "!text-interactive-icon-disabled")}
        onPress={handlePrevious}
        isDisabled={!canGoPrevious}
        label="Previous page"
      />
      <IconButton
        icon={ChevronRightIcon}
        variant="transparent"
        size="small"
        className="!p-1"
        iconClassName={cx("w-5 h-5 text-text-default-primary", !canGoNext && "!text-interactive-icon-disabled")}
        onPress={handleNext}
        isDisabled={!canGoNext}
        label="Next page"
      />
    </div>
  );
};
